import type { DirectionCard, PreviewCopy, TypeRecommendation } from "../domain.js";
import type { TypeCatalog } from "./type-engine.js";

export const HARMONY_ENGINE_VERSION = "type-set-direction/1.0.0";

/** Compose a Type Set result without inventing a palette, compatibility score,
 * or application promise. The selected recommendation remains engine-owned. */
export const buildDirectionCard = (input: {
  projectId: string;
  name?: string;
  type: TypeRecommendation;
  typeCatalog: TypeCatalog;
  previewCopy?: PreviewCopy;
}): DirectionCard => {
  if (!input.projectId.trim()) throw new Error("Direction card needs a projectId.");
  const selectedType = input.type.recommendations[0] ?? null;
  const selectedRecord = selectedType
    ? input.typeCatalog.systems.find((system) => system.id === selectedType.systemId) ?? null
    : null;
  const rulesFired: string[] = [];
  const guidance: string[] = [];
  const warnings: string[] = [];

  if (!selectedType) {
    rulesFired.push("T00_NO_SURVIVING_DIRECTION");
    warnings.push("No type direction survived the declared constraints. Change an answer or bring an exact mandatory font file.");
  } else {
    rulesFired.push("T10_ROLES_ASSIGNED");
    guidance.push("Headlines carry the voice. Body copy carries the reading. Labels and numbers keep the deck navigable.");
    if (selectedRecord?.character === "expressive") {
      rulesFired.push("T11_EXPRESSIVE_DISPLAY");
      guidance.push("Let the display face have the dramatic moments. Repetition is where charm becomes shouting.");
    }
    if (selectedRecord?.candidateStrengths.includes("tabularNumerals")) {
      rulesFired.push("T12_TABULAR_NUMERALS");
      guidance.push("Use tabular figures where values must align; do not mistake tidy columns for a truthful chart.");
    }
  }

  if (input.type.mode === "candidate_demo") {
    rulesFired.push("T90_CANDIDATE_TRUTH");
    warnings.push("This direction still carries candidate-only evidence. Do not present it as a production recommendation.");
  }

  const headline = selectedType
    ? `${selectedType.name}. A voice with a job to do.`
    : "No forced answer. Change the constraint that broke the fit.";

  return {
    schemaVersion: "1.0.0",
    product: "Type Set",
    projectId: input.projectId,
    name: input.name?.trim() || "Untitled type direction",
    type: input.type,
    selected: { typeSystemId: selectedType?.systemId ?? null },
    relationship: {
      headline,
      principle: "The recommendation assigns distinct jobs to display, reading, and utility type. It never pretends browser rendering proves another application.",
      rulesFired,
      guidance,
      warnings,
    },
    previewCopy: input.previewCopy ?? {},
    truth: {
      typeStatus: input.type.outcome !== "recommendation" ? "unavailable" : input.type.mode === "candidate_demo" ? "candidate_demo" : "verified_scope",
      claims: [
        "Typography output follows ordered gates and exposes removals, caveats, and evidence state.",
        "Every font shown in the production fitting room resolves to a checked local open-source file.",
        "The slide rig measures the supplied copy against named layout limits in this browser.",
      ],
      limits: [
        "Browser rendering does not prove installation, line breaks, or export fidelity in PowerPoint, Keynote, Google Slides, Canva, or another application.",
        "A type direction cannot prove story quality, persuasion, cultural meaning, or universal accessibility.",
      ],
    },
  };
};
